/**
 * §15 telemetry and §14.7 budget monitoring.
 *
 * Three things, one file.
 *
 *  1. **The counter struct and its ring** (§14.7). Fifteen numbers per frame in
 *     column-major typed arrays, 600 frames deep. {@link Telemetry.record}
 *     allocates nothing: the caller owns one {@link PerfCounters} object, fills
 *     it every frame and hands the same object back forever.
 *  2. **`perf_sample` and `perf_cliff`** (§15.3, §15.4 F4). A sample every 60
 *     ticks carrying the percentiles over the ring; a cliff whenever frame time
 *     stays over 16.6 ms for 60 consecutive frames.
 *  3. **The sink.** Local only, per spine §8 and §15.1: a JSONL ring in memory,
 *     exported by a user-initiated download. Nothing is transmitted, no analytics
 *     SDK is imported, and no network call exists anywhere in this file.
 *
 * §15.1 is also a determinism constraint: telemetry consumes no RNG stream and
 * never writes back into sim state, so the same seed and the same inputs produce
 * the same run with telemetry on or off. Everything here reads.
 *
 * The §15.3 registry names `perf_sample`, `perf_cliff` and `pool_exhausted` as
 * bus events. Only `pool_exhausted` exists in `EventPayloads` today and
 * `src/core/events.ts` belongs to the integrator, so the two perf types are
 * emitted on this module's own listener list instead. Adding them to
 * `EventPayloads` and pointing {@link Telemetry.emit} at the bus is a two-line
 * change whenever the integrator wants it.
 */

import { perf } from './perf';

/** §14.7 per-frame counters. One instance, refilled every frame by the caller. */
export interface PerfCounters {
  frame_ms: number;
  sim_ms: number;
  gpu_ms: number;
  draw_calls: number;
  enemies_alive: number;
  projectiles_alive: number;
  particles_alive: number;
  hash_candidate_checks: number;
  steps_dropped: number;
  degradation_rung: number;
  /** §14.1 B8: exactly the player aura and the boss light. Asserted ≤ 2. */
  lights_active: number;
  pool_hw_enemies: number;
  pool_hw_projectiles: number;
  pool_hw_pickups: number;
  pool_hw_particles: number;
}

export function createCounters(): PerfCounters {
  return {
    frame_ms: 0,
    sim_ms: 0,
    gpu_ms: 0,
    draw_calls: 0,
    enemies_alive: 0,
    projectiles_alive: 0,
    particles_alive: 0,
    hash_candidate_checks: 0,
    steps_dropped: 0,
    degradation_rung: 0,
    lights_active: 0,
    pool_hw_enemies: 0,
    pool_hw_projectiles: 0,
    pool_hw_pickups: 0,
    pool_hw_particles: 0,
  };
}

/** §14.7 aggregation window. 10 s at 60 Hz. */
export const RING_FRAMES = 600;
/** §15.3: a `perf_sample` every 60 ticks. */
export const PERF_SAMPLE_TICKS = 60;
/** §15.4 F4 `cliff_frame_ms`. Not in the config registry; owner: perf. */
export const CLIFF_FRAME_MS = 16.6;
/** §15.4 F4 `cliff_sustain_frames`. */
export const CLIFF_SUSTAIN_FRAMES = 60;
/** §14.1 B8 assertion bound. */
export const MAX_DYNAMIC_LIGHTS = 2;
/** JSONL sink depth. Oldest lines fall off; nothing leaves the machine either way. */
export const SINK_LINES = 8192;
/** Frames between repeats of the same budget violation, so one breach is one event. */
const VIOLATION_COOLDOWN_FRAMES = 600;

/** §15.3 registry subset this module emits, plus the §14.7 budget assertion. */
export type TelemetryType =
  | 'run_start'
  | 'run_end'
  | 'perf_sample'
  | 'perf_cliff'
  | 'pool_exhausted'
  | 'budget_violation'
  | 'balance_flag';

/** §15.2 envelope. `payload` round-trips through JSON with no custom formatting. */
export interface TelemetryEvent {
  event_id: number;
  tick: number;
  run_seed: number;
  type: TelemetryType;
  payload: Record<string, unknown>;
}

export interface PerfSamplePayload extends Record<string, unknown> {
  frame_ms_p50: number;
  frame_ms_p95: number;
  frame_ms_p99: number;
  frame_ms_max: number;
  sim_ms_p95: number;
  gpu_ms_p95: number;
  draw_calls_max: number;
  enemies_alive: number;
  projectiles_alive: number;
  particles_alive: number;
  pool_highwater: {
    enemies: number;
    projectiles: number;
    pickups: number;
    particles: number;
  };
  hash_candidate_checks: number;
  steps_dropped: number;
  degradation_rung: number;
  lights_active: number;
  device_tier: string;
}

export interface FrameRecord {
  frame: number;
  tick: number;
  frame_ms: number;
  sim_ms: number;
  gpu_ms: number;
  draw_calls: number;
  enemies_alive: number;
  projectiles_alive: number;
  particles_alive: number;
  hash_candidate_checks: number;
  steps_dropped: number;
  degradation_rung: number;
  lights_active: number;
}

export interface Histogram {
  count: number;
  avg: number;
  p50: number;
  p95: number;
  p99: number;
  max: number;
}

export type TelemetryListener = (ev: TelemetryEvent) => void;

const EMPTY_HISTOGRAM: Histogram = { count: 0, avg: 0, p50: 0, p95: 0, p99: 0, max: 0 };

export class Telemetry {
  /** §15.1: compiled in for dev/bench, off by default in release. */
  enabled = true;
  /** §15.3 `telemetry.verbose` — per-hit logging, headless only. */
  verbose = false;

  runSeed = 0;
  contentHash = '';
  version = '';

  // ── the §14.7 ring, column-major so `record` never allocates ──
  private readonly ticks = new Int32Array(RING_FRAMES);
  private readonly frameMs = new Float64Array(RING_FRAMES);
  private readonly simMs = new Float64Array(RING_FRAMES);
  private readonly gpuMs = new Float64Array(RING_FRAMES);
  private readonly drawCalls = new Int32Array(RING_FRAMES);
  private readonly enemies = new Int32Array(RING_FRAMES);
  private readonly projectiles = new Int32Array(RING_FRAMES);
  private readonly particles = new Int32Array(RING_FRAMES);
  private readonly hashChecks = new Int32Array(RING_FRAMES);
  private readonly stepsDropped = new Int32Array(RING_FRAMES);
  private readonly rungs = new Int32Array(RING_FRAMES);
  private readonly lights = new Int32Array(RING_FRAMES);
  private readonly scratch = new Float64Array(RING_FRAMES);

  /** Monotonic frames recorded since {@link reset}. The ring holds the last 600. */
  private cursor = 0;
  private filled = 0;

  // ── §15.4 F4 cliff detection ──
  private cliffRun = 0;
  private cliffWorstMs = 0;
  private cliffStartTick = 0;
  private cliffs = 0;

  // ── high-water marks, kept across the ring's horizon ──
  private hwEnemies = 0;
  private hwProjectiles = 0;
  private hwPickups = 0;
  private hwParticles = 0;
  private maxDrawCalls = 0;
  private maxLights = 0;
  private framesOver33 = 0;
  private totalSteps = 0;

  private lastSampleTick = -PERF_SAMPLE_TICKS;
  private lastViolationFrame = -VIOLATION_COOLDOWN_FRAMES;
  private nextEventId = 1;

  private readonly listeners: TelemetryListener[] = [];
  private readonly sink: string[] = [];
  private sinkHead = 0;
  private sinkLen = 0;

  get frameCursor(): number {
    return this.cursor;
  }

  get cliffCount(): number {
    return this.cliffs;
  }

  get framesOverBudget(): number {
    return this.framesOver33;
  }

  /** §15.2 — the first line of every log is `run_start`. */
  begin(runSeed: number, contentHash: string, version: string): void {
    this.reset();
    this.runSeed = runSeed >>> 0;
    this.contentHash = contentHash;
    this.version = version;
    this.emit('run_start', 0, {
      version,
      content_hash: contentHash,
      device_tier: perf.tier,
      caps: { ...perf.caps },
    });
  }

  end(tick: number, outcome: string, durationS: number): void {
    this.emit('run_end', tick, { tick, outcome, duration_s: durationS });
  }

  /**
   * One frame of §14.7 counters. Allocates nothing.
   *
   * The only object churn on this path is the `perf_sample` payload once per 60
   * ticks and a `perf_cliff` payload when one fires — both of which §15.3 asks
   * for by name, and neither of which is per-frame.
   */
  record(c: PerfCounters, tick: number): void {
    if (!this.enabled) return;

    const i = this.cursor % RING_FRAMES;
    this.ticks[i] = tick | 0;
    this.frameMs[i] = c.frame_ms;
    this.simMs[i] = c.sim_ms;
    this.gpuMs[i] = c.gpu_ms;
    this.drawCalls[i] = c.draw_calls | 0;
    this.enemies[i] = c.enemies_alive | 0;
    this.projectiles[i] = c.projectiles_alive | 0;
    this.particles[i] = c.particles_alive | 0;
    this.hashChecks[i] = c.hash_candidate_checks | 0;
    this.stepsDropped[i] = c.steps_dropped | 0;
    this.rungs[i] = c.degradation_rung | 0;
    this.lights[i] = c.lights_active | 0;
    this.cursor++;
    if (this.filled < RING_FRAMES) this.filled++;

    if (c.pool_hw_enemies > this.hwEnemies) this.hwEnemies = c.pool_hw_enemies;
    if (c.pool_hw_projectiles > this.hwProjectiles) this.hwProjectiles = c.pool_hw_projectiles;
    if (c.pool_hw_pickups > this.hwPickups) this.hwPickups = c.pool_hw_pickups;
    if (c.pool_hw_particles > this.hwParticles) this.hwParticles = c.pool_hw_particles;
    if (c.draw_calls > this.maxDrawCalls) this.maxDrawCalls = c.draw_calls;
    if (c.lights_active > this.maxLights) this.maxLights = c.lights_active;
    if (c.frame_ms > 33) this.framesOver33++;
    this.totalSteps = c.steps_dropped;

    // §14.1 B8 — the assertion §14.7 asks for, as an event rather than a throw:
    // a mis-lit frame is a bug to find in the log, not a reason to kill the run.
    if (c.lights_active > MAX_DYNAMIC_LIGHTS && this.cursor - this.lastViolationFrame >= VIOLATION_COOLDOWN_FRAMES) {
      this.lastViolationFrame = this.cursor;
      this.emit('budget_violation', tick, {
        limit: 'B8',
        counter: 'lights_active',
        value: c.lights_active,
        threshold: MAX_DYNAMIC_LIGHTS,
      });
    }

    this.detectCliff(c.frame_ms, tick);

    if (tick - this.lastSampleTick >= PERF_SAMPLE_TICKS) {
      this.lastSampleTick = tick;
      this.emitSample(tick, c);
    }
  }

  /** §15.4 F4: frame_ms over 16.6 for 60 consecutive frames is a cliff. */
  private detectCliff(frameMs: number, tick: number): void {
    if (frameMs > CLIFF_FRAME_MS) {
      if (this.cliffRun === 0) {
        this.cliffStartTick = tick;
        this.cliffWorstMs = frameMs;
      } else if (frameMs > this.cliffWorstMs) {
        this.cliffWorstMs = frameMs;
      }
      this.cliffRun++;
      if (this.cliffRun === CLIFF_SUSTAIN_FRAMES) {
        this.cliffs++;
        this.emit('perf_cliff', tick, {
          frames: this.cliffRun,
          worst_ms: this.cliffWorstMs,
          window_start_tick: this.cliffStartTick,
        });
      }
      return;
    }
    this.cliffRun = 0;
    this.cliffWorstMs = 0;
  }

  private emitSample(tick: number, c: PerfCounters): void {
    const frame = this.histogram(this.frameMs);
    const payload: PerfSamplePayload = {
      frame_ms_p50: frame.p50,
      frame_ms_p95: frame.p95,
      frame_ms_p99: frame.p99,
      frame_ms_max: frame.max,
      sim_ms_p95: this.percentile(this.simMs, 0.95),
      gpu_ms_p95: this.percentile(this.gpuMs, 0.95),
      draw_calls_max: this.maxIntOf(this.drawCalls),
      enemies_alive: c.enemies_alive,
      projectiles_alive: c.projectiles_alive,
      particles_alive: c.particles_alive,
      pool_highwater: {
        enemies: this.hwEnemies,
        projectiles: this.hwProjectiles,
        pickups: this.hwPickups,
        particles: this.hwParticles,
      },
      hash_candidate_checks: c.hash_candidate_checks,
      steps_dropped: c.steps_dropped,
      degradation_rung: c.degradation_rung,
      lights_active: c.lights_active,
      device_tier: perf.tier,
    };
    this.emit('perf_sample', tick, payload);
  }

  /** §15.2 envelope in, JSONL ring and listeners out. Never a network call. */
  emit(type: TelemetryType, tick: number, payload: Record<string, unknown>): void {
    if (!this.enabled) return;
    const ev: TelemetryEvent = {
      event_id: this.nextEventId++,
      tick: tick | 0,
      run_seed: this.runSeed,
      type,
      payload,
    };
    this.write(ev);
    for (let i = 0; i < this.listeners.length; i++) this.listeners[i](ev);
  }

  private write(ev: TelemetryEvent): void {
    let line: string;
    try {
      line = JSON.stringify(ev);
    } catch {
      // A payload that will not serialize is a caller bug, not a run-ending one.
      line = JSON.stringify({ event_id: ev.event_id, tick: ev.tick, run_seed: ev.run_seed, type: ev.type });
    }
    this.sink[this.sinkHead] = line;
    this.sinkHead = this.sinkHead + 1 === SINK_LINES ? 0 : this.sinkHead + 1;
    if (this.sinkLen < SINK_LINES) this.sinkLen++;
  }

  on(fn: TelemetryListener): () => void {
    this.listeners.push(fn);
    return () => {
      const i = this.listeners.indexOf(fn);
      if (i >= 0) this.listeners.splice(i, 1);
    };
  }

  // ─────────────────────────────────────────────────────────── read-out paths ──

  /** Frame-time histogram over the ring. Cold path; the bench and the overlay call it. */
  frameHistogram(): Histogram {
    return this.histogram(this.frameMs);
  }

  simHistogram(): Histogram {
    return this.histogram(this.simMs);
  }

  gpuHistogram(): Histogram {
    return this.histogram(this.gpuMs);
  }

  /**
   * Plain records for frames `[from, cursor)`, clipped to what the ring still
   * holds. The bench harness polls this every few seconds and accumulates, which
   * is how a 600-frame ring covers a 3,600-frame run.
   */
  drainFrames(from: number): FrameRecord[] {
    const out: FrameRecord[] = [];
    const oldest = this.cursor - this.filled;
    let start = from < oldest ? oldest : from;
    if (start < 0) start = 0;
    for (let f = start; f < this.cursor; f++) {
      const i = f % RING_FRAMES;
      out.push({
        frame: f,
        tick: this.ticks[i],
        frame_ms: this.frameMs[i],
        sim_ms: this.simMs[i],
        gpu_ms: this.gpuMs[i],
        draw_calls: this.drawCalls[i],
        enemies_alive: this.enemies[i],
        projectiles_alive: this.projectiles[i],
        particles_alive: this.particles[i],
        hash_candidate_checks: this.hashChecks[i],
        steps_dropped: this.stepsDropped[i],
        degradation_rung: this.rungs[i],
        lights_active: this.lights[i],
      });
    }
    return out;
  }

  /** Everything the §C.3 artifact and the dev overlay need in one object. */
  snapshot(): Record<string, unknown> {
    return {
      run_seed: this.runSeed,
      content_hash: this.contentHash,
      version: this.version,
      frames: this.cursor,
      frame_ms: this.frameHistogram(),
      sim_ms: this.simHistogram(),
      gpu_ms: this.gpuHistogram(),
      draw_calls_max: this.maxDrawCalls,
      lights_max: this.maxLights,
      frames_over_33ms: this.framesOver33,
      steps_dropped: this.totalSteps,
      perf_cliffs: this.cliffs,
      pool_highwater: {
        enemies: this.hwEnemies,
        projectiles: this.hwProjectiles,
        pickups: this.hwPickups,
        particles: this.hwParticles,
      },
      counts: {
        enemies_p50: this.percentileInt(this.enemies, 0.5),
        projectiles_p50: this.percentileInt(this.projectiles, 0.5),
        particles_p50: this.percentileInt(this.particles, 0.5),
        enemies_max: this.maxIntOf(this.enemies),
        projectiles_max: this.maxIntOf(this.projectiles),
        particles_max: this.maxIntOf(this.particles),
      },
      degradation_rung_max: this.maxIntOf(this.rungs),
      perf: perf.snapshot(),
    };
  }

  /** §15.1 export: the whole ring as JSONL. In-memory string, no I/O. */
  exportJsonl(): string {
    const oldest = this.sinkLen < SINK_LINES ? 0 : this.sinkHead;
    let out = '';
    for (let i = 0; i < this.sinkLen; i++) {
      out += this.sink[(oldest + i) % SINK_LINES];
      out += '\n';
    }
    return out;
  }

  /**
   * User-initiated file download (§15.1). This is the only way telemetry leaves
   * the process, and it goes to the player's own disk.
   */
  download(filename = 'lumen-telemetry.jsonl'): void {
    if (typeof document === 'undefined' || typeof URL === 'undefined') return;
    const blob = new Blob([this.exportJsonl()], { type: 'application/x-ndjson' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  reset(): void {
    this.cursor = 0;
    this.filled = 0;
    this.cliffRun = 0;
    this.cliffWorstMs = 0;
    this.cliffStartTick = 0;
    this.cliffs = 0;
    this.hwEnemies = 0;
    this.hwProjectiles = 0;
    this.hwPickups = 0;
    this.hwParticles = 0;
    this.maxDrawCalls = 0;
    this.maxLights = 0;
    this.framesOver33 = 0;
    this.totalSteps = 0;
    this.lastSampleTick = -PERF_SAMPLE_TICKS;
    this.lastViolationFrame = -VIOLATION_COOLDOWN_FRAMES;
    this.nextEventId = 1;
    this.sink.length = 0;
    this.sinkHead = 0;
    this.sinkLen = 0;
  }

  // ───────────────────────────────────────────────────────────────── internals ──

  private histogram(col: Float64Array): Histogram {
    const n = this.filled;
    if (n === 0) return { ...EMPTY_HISTOGRAM };
    const s = this.scratch;
    let sum = 0;
    for (let i = 0; i < n; i++) {
      s[i] = col[i];
      sum += col[i];
    }
    sortPrefix(s, n);
    return {
      count: n,
      avg: sum / n,
      p50: s[pickIndex(0.5, n)],
      p95: s[pickIndex(0.95, n)],
      p99: s[pickIndex(0.99, n)],
      max: s[n - 1],
    };
  }

  private percentile(col: Float64Array, q: number): number {
    const n = this.filled;
    if (n === 0) return 0;
    const s = this.scratch;
    for (let i = 0; i < n; i++) s[i] = col[i];
    sortPrefix(s, n);
    return s[pickIndex(q, n)];
  }

  private percentileInt(col: Int32Array, q: number): number {
    const n = this.filled;
    if (n === 0) return 0;
    const s = this.scratch;
    for (let i = 0; i < n; i++) s[i] = col[i];
    sortPrefix(s, n);
    return s[pickIndex(q, n)];
  }

  private maxIntOf(col: Int32Array): number {
    const n = this.filled;
    let m = 0;
    for (let i = 0; i < n; i++) if (col[i] > m) m = col[i];
    return m;
  }
}

/** Insertion sort over the first `n` of a preallocated scratch. No `Array.sort`. */
function sortPrefix(s: Float64Array, n: number): void {
  for (let i = 1; i < n; i++) {
    const v = s[i];
    let j = i - 1;
    while (j >= 0 && s[j] > v) {
      s[j + 1] = s[j];
      j--;
    }
    s[j + 1] = v;
  }
}

function pickIndex(q: number, n: number): number {
  let k = Math.round(q * (n - 1));
  if (k < 0) k = 0;
  else if (k >= n) k = n - 1;
  return k;
}

/** The one sink. Local only, per spine §8. */
export const telemetry = new Telemetry();
